import React, { useRef } from 'react';

type InputSnapshot = { text: string; selectionStart: number | null; selectionEnd: number | null };

type Options = {
  /** Switches the whole behaviour on. When false the hook returns empty prop sets and does nothing. */
  enabled: boolean;
  min?: number;
  max?: number;
  maxFractionDigits: number;
};

const DECIMAL_TOLERANCE = 1e-9;

const hasMoreDecimalsThan = (value: number, maxFractionDigits: number) => {
  const scaled = value * 10 ** maxFractionDigits;
  return Math.abs(scaled - Math.round(scaled)) > DECIMAL_TOLERANCE;
};

/**
 * Makes a PrimeReact InputNumber reject a typed, pasted or stepped value outside min/max (or with more than
 * maxFractionDigits decimals) instead of clamping or rounding it: the input keeps its previous text.
 *
 * InputNumber only reports a typed or pasted value to onValueChange on blur, and by then it has clamped it.
 * Its onChange runs on every insert with the unclamped value, so the check is done there and the text is put
 * back, which keeps an out-of-range value from ever reaching the caller.
 *
 * Spread `wrapperProps` on the element that wraps the InputNumber, and `inputProps` on the InputNumber.
 */
const useRejectOutOfRange = ({ enabled, min, max, maxFractionDigits }: Options) => {
  const inputElementRef = useRef<HTMLInputElement | null>(null);
  const snapshotRef = useRef<InputSnapshot | null>(null);

  const rememberInput = () => {
    const input = inputElementRef.current;
    if (!input) {
      return;
    }
    snapshotRef.current = { text: input.value, selectionStart: input.selectionStart, selectionEnd: input.selectionEnd };
  };

  // On an empty input InputNumber formats a pasted 15.555 to 15.56 before it reports the value, so a value with
  // too many decimals has to be recognised on the clipboard text itself.
  const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    rememberInput();
    const pasted = Number(event.clipboardData.getData('Text').trim().replace(',', '.'));
    if (Number.isFinite(pasted) && hasMoreDecimalsThan(pasted, maxFractionDigits)) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const isRejected = (newValue: number | null) => {
    if (newValue === null) {
      return false;
    }
    return (
      (min !== undefined && newValue < min) ||
      (max !== undefined && newValue > max) ||
      hasMoreDecimalsThan(newValue, maxFractionDigits)
    );
  };

  const handleChange = (newValue: number | null) => {
    const input = inputElementRef.current;
    const snapshot = snapshotRef.current;
    if (!isRejected(newValue) || !input || !snapshot) {
      return;
    }
    input.value = snapshot.text;
    input.setSelectionRange(snapshot.selectionStart, snapshot.selectionEnd);
  };

  // The arrow keys step the value, and InputNumber clamps the stepped result to min/max.
  const blockSteppingOutOfRange = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const input = inputElementRef.current;
    if (!input || (event.key !== 'ArrowUp' && event.key !== 'ArrowDown')) {
      return;
    }
    const current = Number(input.value.replace(',', '.')) || 0;
    const stepped = event.key === 'ArrowUp' ? current + 1 : current - 1;
    if ((min !== undefined && stepped < min) || (max !== undefined && stepped > max)) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  if (!enabled) {
    return { wrapperProps: {}, inputProps: {} };
  }

  return {
    wrapperProps: {
      onKeyPressCapture: rememberInput,
      onPasteCapture: handlePaste,
      onKeyDownCapture: blockSteppingOutOfRange,
      // Dropped text would bypass the keypress and paste handlers of InputNumber
      onDropCapture: (event: React.DragEvent<HTMLDivElement>) => event.preventDefault(),
    },
    inputProps: {
      inputRef: inputElementRef,
      onChange: (e: { value: number | null }) => handleChange(e.value),
    },
  };
};

export default useRejectOutOfRange;
